import { FormikErrors, FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from 'hooks';
import { ConsignmentDetails } from 'types';
import { Button, Checkbox, Heading, Product } from 'components';
import { formValidations, messages, storedAttendance } from 'helpers';
import { FontSize, StepsEnum, TEST_IDS } from 'commons';
import { Ean } from './components';
import * as S from './styles';

interface Props {
  consignment: ConsignmentDetails;
  setCurrentStep: (step: StepsEnum) => void;
  setShowRefuse: (value: boolean) => void;
}

interface EanForm {
  eanList: { ean: string; hasFreeBag: boolean }[];
}

export function StepConfirmEan({
  consignment,
  setCurrentStep,
  setShowRefuse,
}: Props) {
  const { user } = useAuth();
  const { isAdmin } = user;

  const { code: consignmentCode, entries } = consignment;

  function persistEAN({
    ean,
    productCode,
  }: {
    ean: string;
    productCode: string;
  }): void {
    const currentAttendance = storedAttendance.getCurrent({
      entries,
      consignmentCode,
    });

    const newAttendanceProducts = currentAttendance.products.map(
      (attendanceProduct) =>
        attendanceProduct.code === productCode
          ? { code: productCode, ean }
          : attendanceProduct
    );

    const newAttendance = {
      ...currentAttendance,
      products: newAttendanceProducts,
    };

    storedAttendance.persist(newAttendance);
  }

  function persistStep(step: StepsEnum) {
    const currentAttendance = storedAttendance.getCurrent({
      entries,
      consignmentCode,
    });

    const newAttendance = {
      ...currentAttendance,
      step: step,
    };

    storedAttendance.persist(newAttendance);
  }

  const eanValidation = Yup.object().shape({
    eanList: Yup.array().of(
      Yup.object().shape({
        ean: Yup.string().required('EAN'),
      })
    ),
  });

  const formEan: FormikProps<EanForm> = useFormik({
    initialValues: ((): EanForm => {
      const currentAttendance = storedAttendance.getCurrent({
        entries,
        consignmentCode,
      });

      const eanList = entries.map((entry) => {
        const storedProduct = currentAttendance?.products.find(
          (attendanceProduct) => {
            return entry.product.code === attendanceProduct.code;
          }
        );

        return {
          ean: storedProduct?.ean || '',
          hasFreeBag: !entry.hasFreeBag,
        };
      });

      return { eanList };
    })(),

    validationSchema: eanValidation,
    validate: (values) => {
      formValidations.validationSchema({ schema: eanValidation, values });
    },

    onSubmit: async (values) => {
      const { eanList } = values;

      const areAllEanCorrect = entries.every((entry, index) => {
        return entry.product.ean === eanList[index].ean;
      });

      const areAllChecked = eanList.every((ean) => {
        return ean.hasFreeBag;
      });

      if (!areAllEanCorrect) {
        messages([{ errorCode: 'FRONT-RE-EAN-INVALID' }]);
        return;
      }

      if (!areAllChecked) {
        messages([
          { errorCode: 'FRONT-RE-NECESSARY_FIELD' },
          { subject: 'ESTOU CIENTE', subjectType: 'field' },
        ]);
        return;
      }

      const currentAttendance = storedAttendance.getCurrent({
        entries,
        consignmentCode,
      });

      currentAttendance.products.forEach((product, index) => {
        product.ean = eanList[index].ean;
      });

      storedAttendance.persist(currentAttendance);

      const { Invoice } = StepsEnum;

      setCurrentStep(Invoice);
      persistStep(Invoice);
    },
  });

  function getInputStatus({
    typedEan,
    ean,
    error,
  }: {
    typedEan: string;
    ean: string;
    error:
      | string
      | FormikErrors<{ ean: string; hasFreeBag: boolean | undefined }>
      | undefined;
  }): 'default' | 'success' | 'error' {
    if (!typedEan && error) return 'error';

    if (!typedEan) return 'default';

    return ean === typedEan ? 'success' : 'error';
  }

  const renderProducts = entries.map((entry, index) => {
    const { ean, code } = entry.product;
    const typedEan = formEan.values.eanList[index].ean;
    const checkedStatus = formEan.values.eanList[index].hasFreeBag;

    const error = formEan.errors.eanList?.[index];

    const statusClass = getInputStatus({ typedEan, ean, error });

    return (
      <Product key={code} entry={entry} status={statusClass}>
        <S.EanContainer>
          <Ean
            name={`eanList[${index}].ean`}
            value={typedEan}
            onBlur={() => {
              persistEAN({ productCode: code, ean: typedEan });
            }}
            onChange={formEan.handleChange}
            disabled={isAdmin}
          />
          {entry.hasFreeBag && (
            <S.CheckboxWrapper>
              <Checkbox
                name={`eanList[${index}].hasFreeBag`}
                id={`eanList[${index}].hasFreeBag`}
                value={checkedStatus.toString()}
                checked={checkedStatus}
                onChange={formEan.handleChange}
                disabled={isAdmin}
              />

              <S.CheckboxLabel htmlFor={`eanList[${index}].hasFreeBag`}>
                Estou ciente que preciso enviar uma embalagem de presente
              </S.CheckboxLabel>
            </S.CheckboxWrapper>
          )}
        </S.EanContainer>
      </Product>
    );
  });

  return (
    <S.Form onSubmit={formEan.handleSubmit}>
      <Heading size={FontSize.font14}>Itens do pedido</Heading>

      <S.ProductsWrapper>{renderProducts}</S.ProductsWrapper>

      <S.ButtonsWrapper>
        <Button
          data-testid={TEST_IDS.REFUSE_ATTENDANCE_BUTTON}
          variant="outline-secondary"
          disabled={formEan.isSubmitting || isAdmin}
          type="button"
          onClick={() => setShowRefuse(true)}
        >
          Recusar pedido
        </Button>

        <Button
          data-testid={TEST_IDS.CONFIRM_ATTENDANCE_BUTTON}
          type="submit"
          disabled={formEan.isSubmitting || isAdmin}
        >
          CONTINUAR
        </Button>
      </S.ButtonsWrapper>
    </S.Form>
  );
}
